import { finalLeft, releaseStepLabel } from './logic';
import { NOW_MIN, RELEASE_STEPS } from './fixtures';
import type { BlAmend, BlDraft, BlFinal, BlRelease, LoiTemplate, Manifest, OriginalDoc } from './types';

// Dữ liệu hiển thị của chi tiết Mint-BL (code board: khối DRAWER, rkind draft | final | rel) dùng cho BlDetailPage. Tiền ẩn.

export type BlRec =
  | { kind: 'draft'; r: BlDraft }
  | { kind: 'final'; r: BlFinal }
  | { kind: 'rel'; r: BlRelease }
  | { kind: 'amd'; r: BlAmend }
  | { kind: 'loi'; r: LoiTemplate }
  | { kind: 'orig'; r: OriginalDoc }
  | { kind: 'mnf'; r: Manifest };

const RESP_TX = { none: 'Chưa gửi', wait: 'Chờ xác nhận', fix: 'Yêu cầu sửa', ok: 'Đã xác nhận' } as const;

type View = { kind: string; title: string; sub: string; facts: [string, string][]; act: string; actDone: boolean; actOff?: string };

export function view(rec: BlRec): View {
  if (rec.kind === 'draft') {
    const r = rec.r;
    const block = r.loi === 'missing' && (r.type === 'Surrender' || r.type === 'Telex');
    return {
      kind: 'Draft B/L', title: r.hbl, sub: `${r.job} · ${r.cus} → ${r.cne}`,
      facts: [['Hãng', r.car], ['Loại bill', r.type], ['Nguồn', r.src], ['Hạn', r.due], ['Gửi lần cuối', r.sent], ['Khách phản hồi', r.resp === 'fix' ? `${RESP_TX.fix}: ${r.fix ?? ''}` : RESP_TX[r.resp]], ['LOI', r.loi === 'missing' ? `Thiếu LOI ${r.car}` : r.loi === 'ok' ? 'Đã kèm mẫu hãng' : 'Không cần'], ['Phiên bản', r.ver ? `v${r.ver}` : 'Chưa gửi']],
      act: r.resp === 'ok' ? 'Đã xác nhận' : block ? 'Thiếu LOI' : r.resp === 'wait' ? 'Gửi lại' : 'Gửi Draft', actDone: r.resp === 'ok', actOff: block || r.loi === 'missing' ? 'Tạo LOI từ mẫu trước' : undefined,
    };
  }
  if (rec.kind === 'final') {
    const r = rec.r;
    const l = finalLeft(r, NOW_MIN);
    return {
      kind: 'Final B/L', title: r.hbl, sub: `${r.job} · ${r.cus}`,
      facts: [['Tàu', r.vsl], ['ATD', r.atd], ['Hạn gửi', r.due], ['Còn lại', l.kind === 'sent' ? `Đã gửi ${r.sentAt ?? ''}`.trim() : l.kind === 'overdue' ? 'Quá hạn' : `${l.hours}h${String(l.minutes).padStart(2, '0')}′ giờ làm việc`], ['Ghi chú', r.note ?? '']],
      act: r.st === 'sent' ? 'Đã gửi' : r.st === 'fail' ? 'Gửi lại' : 'Gửi Final', actDone: r.st === 'sent',
    };
  }
  if (rec.kind === 'amd') {
    const r = rec.r;
    // Phí AMD là tiền → ẩn (money = hide)
    return { kind: 'Sửa B/L sau phát hành', title: r.hbl, sub: r.cus, facts: [['Job', r.job], ['Nội dung', r.what], ['Lỗi do', r.by]], act: 'Đóng', actDone: true };
  }
  if (rec.kind === 'loi') {
    const r = rec.r;
    return { kind: 'Mẫu LOI', title: `${r.car} · ${r.kind}`, sub: 'Chủ mẫu: Trưởng CS (giao tạm: THUỶ)', facts: [['Hãng', r.car], ['Loại', r.kind], ['Phiên bản', r.ver], ['Hiệu lực', r.from], ['Đã dùng', `${r.uses} lần`]], act: 'Đóng', actDone: true };
  }
  if (rec.kind === 'orig') {
    const r = rec.r;
    return { kind: 'Chứng từ gốc', title: r.no, sub: r.cus, facts: [['Số', r.no], ['Job', r.job], ['Đang ở', r.at], ['Vận đơn', r.trk], ['Ký nhận', r.sign]], act: 'Đóng', actDone: true };
  }
  if (rec.kind === 'mnf') {
    const r = rec.r;
    return { kind: 'Manifest', title: r.hbl, sub: r.dest, facts: [['Job', r.job], ['Loại khai', r.kind], ['Hạn', r.due]], act: 'Đóng', actDone: true };
  }
  const r = rec.r;
  return {
    kind: `Release ${r.type}`, title: r.job, sub: `${r.cus} · yêu cầu qua ${r.via} lúc ${r.when}`,
    facts: [['Bước hiện tại', releaseStepLabel(r, RELEASE_STEPS)], ['Cấp duyệt', r.lvl], ['Điều khoản', r.terms], ['Còn thiếu', r.missing.join(' · ') || 'Đủ'], ...(r.rej ? [['Lý do từ chối', r.rej.why] as [string, string]] : [])],
    act: r.step >= 5 ? 'Đã release' : r.step === 4 ? 'Gửi Release' : r.step === 2 && r.mine ? 'Duyệt' : 'Đóng', actDone: r.step >= 5,
  };
}
